/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import { FC, useEffect } from "react";
import { LoaderLayout } from "../../components/atoms/loader/LoaderLayout";
import { NotFoundError } from "features/NotFoundError";
import { useDispatch, useSelector } from "react-redux";
import { Store } from "store";
import { useQuery } from "../../utility/useQuery";
import { createUrl } from "utility/createUrl";
import { StreamingStandby } from "./StreamingStandby";
import { useGetRestrictedStreamingKey } from "./useGetRestrictedStreamingKey";
import { useGetRestrictedStreamingKeysForVideoGroupSet } from "./useGetRestrictedStreamingKeysForVideoGroupSet";
import { useParams } from "react-router";
import { useGetEvent } from "hooks/event/useGetEvent";
import { getEventDisplayInfo } from "utility";
import { useGetStreamingVideo } from "./useGetStreamingVideo";
import { useGetStreamingVideos } from "./useGetStreamingVideos";
import { useGetPlayerSetting } from "./useGetPlayerSetting";
import { streamingActions } from "modules/streaming";
import { eventActions } from "modules/event";
import {
  StreamingSwitcherProps,
  StreamingSwitcher,
} from "containers/streaming/StreamingSwitcher";
import { useFeatureFlagFromFirestore } from "containers/streaming/useFeatureFlagFromFirestore";

type StreamingLogicProps = {};

/**
 * 関係者限定の配信ページ
 *
 * URL 構造:
 * - videoId 単位:        /streams/p/{videoId}?event={eventId}&code={code}
 * - videoGroupSet 単位:  /streams/p/{mainVideoId}?event={eventId}&code={code}&vgsid={videoGroupSetId}
 *
 * vgsid が指定された場合はマルチアングル（videoGroupSet 単位）として扱い、
 * 配下の全 video の署名付き Cookie を一括取得してアングル切り替えを可能にする。
 * vid クエリで現在表示する video を指定する（無ければ path の videoId）。
 *
 * @see https://www.notion.so/balusco/cf127373c94744c495144820c6f40ec8
 */
export const RestrictedStreaming: FC<StreamingLogicProps> = () => {
  // URLから必要なパラメータを取得
  const { videoId } = useParams<{ videoId: string }>();
  const eventId = useQuery().get("event") as string;
  const code = useQuery().get("code") as string;
  // 空文字（?vgsid= / ?vid=）は未指定として扱う。
  const vgsidQuery = useQuery().get("vgsid");
  const videoGroupSetId = vgsidQuery ? vgsidQuery : undefined;
  // マルチアングル時の現在表示 video（vid）。空文字は未指定として扱い path の videoId を使う。
  const vidQuery = useQuery().get("vid");
  const requestedVid = vidQuery ? vidQuery : videoId;
  const isVideoGroupSetMode = videoGroupSetId !== undefined;

  // Redux Store からダークモードの設定を取得
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );

  // イベント情報を取得
  const { data: event } = useGetEvent({
    eventId,
  });
  const displayEvent = event ? getEventDisplayInfo(event) : null;

  const { data: playerSetting } = useGetPlayerSetting();
  const vodSetting = playerSetting?.vodSetting;
  const liveSetting = playerSetting?.liveSetting;

  const isPollingEnabled = useFeatureFlagFromFirestore("polling", eventId, {
    enable: "polling",
    disable: "noPolling",
  });

  // --- videoId 単位（従来）の取得 ---
  // vgs モード時は呼び出しを無効化したいが、Hooks は条件付き呼び出しができないため
  // undefined を渡す。各 Hook 側で必須パラメータが未指定なら SWR キーを null にして
  // fetch しない。
  const { data: singleVideo } = useGetStreamingVideo({
    eventId,
    videoId: isVideoGroupSetMode ? undefined : videoId,
  });
  const { data: singleKey, error: singleKeyError } =
    useGetRestrictedStreamingKey({
      eventId,
      videoId: isVideoGroupSetMode ? undefined : videoId,
      code: isVideoGroupSetMode ? undefined : code,
    });

  // --- videoGroupSet 単位（マルチアングル）の取得 ---
  const { data: vgsKeys, error: vgsKeysError } =
    useGetRestrictedStreamingKeysForVideoGroupSet({
      eventId: isVideoGroupSetMode ? eventId : undefined,
      videoGroupSetId,
      code: isVideoGroupSetMode ? code : undefined,
    });
  // 一括取得できた全 video の EventVideo を取得して eventVideoMap を構築する
  const vgsVideoIds =
    vgsKeys?.ok === true ? Object.keys(vgsKeys.value.streamingSources) : [];
  const { data: vgsVideoMap, error: vgsVideoMapError } = useGetStreamingVideos({
    eventId: isVideoGroupSetMode ? eventId : undefined,
    videoIds: vgsVideoIds,
  });

  // --- モードごとに StreamingContext へ渡す値を決定する ---
  // EventVideo 取得失敗（vgsVideoMapError）も拾い、ローディングから復帰できない状態を防ぐ。
  const error = isVideoGroupSetMode
    ? (vgsKeysError ?? vgsVideoMapError)
    : singleKeyError;
  // 関係者限定配信の認証キー取得結果（vgs / videoId 単位のいずれか）
  const restrictedKeyResult = isVideoGroupSetMode ? vgsKeys : singleKey;

  // cookies: videoId -> streamingSource のマップ
  const cookies = (() => {
    if (isVideoGroupSetMode) {
      return vgsKeys?.ok === true ? vgsKeys.value.streamingSources : null;
    }
    return singleKey?.ok === true
      ? { [videoId]: singleKey.value.streamingSource }
      : null;
  })();

  // eventVideoMap: マルチアングルは配下全 video、単体は対象 video のみ
  const eventVideoMap = (() => {
    if (isVideoGroupSetMode) return vgsVideoMap ?? null;
    return singleVideo ? { [videoId]: singleVideo } : null;
  })();

  // クエリ由来の vid は cookies / eventVideoMap に存在するとは限らない。
  // 取得済みの video セットで検証し、無効なら先頭の利用可能 video（無ければ path の videoId）へフォールバックする。
  const availableVideoIds =
    isVideoGroupSetMode && cookies && eventVideoMap
      ? Object.keys(cookies).filter((id) => eventVideoMap[id])
      : [];
  const currentVid = (() => {
    if (!isVideoGroupSetMode) return videoId;
    if (availableVideoIds.includes(requestedVid)) return requestedVid;
    return availableVideoIds[0] ?? videoId;
  })();

  // 現在表示 video の EventVideo（standby 表示で利用）
  const currentVideo = isVideoGroupSetMode
    ? eventVideoMap?.[currentVid]
    : singleVideo;

  const streamingStatus =
    restrictedKeyResult?.ok === true
      ? restrictedKeyResult.value.streamingStatus
      : null;

  /**
   * 配信を閲覧する権限がない時、エラーが返ってくるので、ここの条件分岐が表示される。
   */
  if (error || restrictedKeyResult?.ok === false) {
    return (
      <NotFoundError
        urlToTopPage={createUrl({
          location: window.location.origin,
          searchParams: [],
        })}
      />
    );
  }

  if (
    restrictedKeyResult === undefined ||
    streamingStatus === null ||
    !displayEvent ||
    !cookies ||
    !eventVideoMap ||
    !vodSetting ||
    !liveSetting ||
    isPollingEnabled === undefined
  ) {
    return (
      <div id="contents-layout">
        <LoaderLayout />
      </div>
    );
  }

  // vgsid モードで現在表示する video が cookies / eventVideoMap に解決できない場合、
  // 再生 UI で eventVideoMap[vid] 等が undefined になり実行時エラーになるため、
  // NotFound として早期に弾く。
  if (
    isVideoGroupSetMode &&
    (!cookies[currentVid] || !eventVideoMap[currentVid])
  ) {
    return (
      <NotFoundError
        urlToTopPage={createUrl({
          location: window.location.origin,
          searchParams: [],
        })}
      />
    );
  }

  if (
    ["beforeLive", "beforeVOD", "ended"].includes(streamingStatus) &&
    currentVideo
  ) {
    return (
      <StreamingStandby
        videoId={currentVid}
        streamingStatus={streamingStatus}
        displayEvent={displayEvent}
        currentVideo={currentVideo}
        isDarkModeEnabled={isDarkModeEnabled}
      />
    );
  }

  return (
    <StreamingContext
      url={""}
      cookies={cookies}
      cookiesList={undefined}
      event={displayEvent}
      hasTicket={true}
      vid={currentVid}
      eventVideoMap={eventVideoMap}
      vodSetting={vodSetting}
      liveSetting={liveSetting}
      isDarkModeEnabled={isDarkModeEnabled}
    />
  );
};

/**
 * Streaming 以下のコンポーネントで、 storeに依存しているコンポーネント用に、取得した値をstoreに保存する
 * Streaming コンポーネントを利用するには、以下がstoreに保存されている必要がある
 * - eventVideoMap
 * - displayEvent
 * コメントシャーディングの判定などで利用されている
 *
 * FIXME: ログインしていない場合にコメントを非表示にしているのは、ログインを指定していない状態で
 *  コメント取得などの redux-saga にある処理経由でredux storeに値を保存しようとしてもできないため。原因不明。
 * @see https://www.notion.so/balusco/cf127373c94744c495144820c6f40ec8?pvs=4#38f7b6c4b11a453293efe7c6316eeea8
 */
const StreamingContext: FC<StreamingSwitcherProps> = ({
  children: _,
  isDarkModeEnabled,
  ...props
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state: Store) => state.auth.user);
  useEffect(() => {
    dispatch(
      streamingActions.setStateByKey({
        eventVideoMap: props.eventVideoMap,
      })
    );
    // マルチアングル切り替え UI は Redux の streamingKey.cookies を参照して
    // 表示するアングルを絞り込む。関係者限定配信では getStreamingKey saga を
    // 経由しないため、取得済みの cookies をここで streamingKey として保存する。
    dispatch(
      streamingActions.setStateByKey({
        streamingKey: {
          isError: false,
          msg: "",
          url: "",
          cookies: props.cookies,
        },
      })
    );
    dispatch(
      eventActions.setStateByKey({
        displayEvent: props.event,
      })
    );
    // コメント用のアイコンを作成
    dispatch(streamingActions.createMySmallIconUrl.started());
  }, [dispatch, props.event, props.eventVideoMap, props.cookies]);

  // ログインしていない場合、コメントを非表示にする
  const isHiddenStreamingComment =
    user.uid === undefined ? true : props.event.isHiddenStreamingComment;

  return (
    <StreamingSwitcher
      {...{
        ...props,
        event: {
          ...props.event,
          isHiddenStreamingComment,
        },
        isDarkModeEnabled,
      }}
    />
  );
};
